import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { tap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { HelpersService } from 'src/app/helpers.service'
import { environment } from 'src/environments/environment'

/**
 * Which identity.individual / identity.business_details fields Stripe's
 * account.requirements.currently_due asks for, per country + business type.
 */
export const stripeConnectRequiredFields = {
  AE: {
    company: ['registeredName']
  },
  SA: {
    individual: ['givenName', 'surname', 'dateOfBirth', 'addressLine1', 'addressCity', 'addressPostalCode'],
    company: ['registeredName', 'addressLine1', 'addressCity', 'addressPostalCode']
  },
  EG: {
    individual: ['givenName', 'surname', 'dateOfBirth', 'addressLine1', 'addressCity', 'addressPostalCode'],
    company: ['registeredName', 'addressLine1', 'addressCity', 'addressPostalCode']
  }
}

/** Mirrors stacks-agents' CreateConnectAccountInput (src/app/agents/payment/connect/stripe-connect.types.ts). */
export interface CreateConnectAccountInput {
  projectId: string
  country: string
  businessType: 'individual' | 'company'
  businessStructure?: 'sole_establishment' | 'free_zone_establishment' | 'llc' | 'free_zone_llc'
  email: string
  // Which of these are actually sent is driven by stripeConnectRequiredFields.
  individual?: {
    givenName?: string
    surname?: string
    dateOfBirth?: string
    addressLine1?: string
    addressCity?: string
    addressPostalCode?: string
  }
  business?: {
    registeredName?: string
    addressLine1?: string
    addressCity?: string
    addressPostalCode?: string
  }
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  builderUrl: string
    options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }
  get project_id() { return this.builderService.selectedProject; }
  settings: any
  currency: any = ''
  constructor(
    private builderService: BuilderService,
    private settingsApi: SettingsApiService,
    private helper: HelpersService,
    private http: HttpClient) {
    this.builderUrl = this.helper.getBuilderUrl()
  }

  submitSettings = (setting: any) => {
    return new Observable<any>((observer) => {
      this.getSettings().subscribe((settings: any) => {
        if (settings) {
          setting = { ...settings, ...setting }
        }
        // Serialize to strings first so the creation layer stores identical values to the old direct upsert.
        const fields: Record<string, string> = {}
        for (const [key, value] of Object.entries(setting)) {
          fields[key] = value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')
        }
        this.settingsApi.upsertSettingsByType(String(this.project_id), 'commerce_settings', fields)
          .then(() => {
            observer.next(setting)
            observer.complete()
          })
          .catch((error) => observer.error(error))
      })
    })
  }

  getCurrencySettings = () => {
    return new Observable<any>((observer) => {
      this.settingsApi.getSettingsByType(String(this.project_id), 'commerce_settings')
        .then((result) => {
          const symbol = result?.['currency_symbol']
          const code = result?.['currency']
          this.currency = symbol || code || ''
          observer.next(this.currency)
          observer.complete()
        })
        .catch(() => { this.currency = ''; observer.next(''); observer.complete() })
    })
  }

  getSettings = () => {
    return new Observable<any>((observer) => {
      this.settingsApi.getSettingsByType(String(this.project_id), 'commerce_settings')
        .then((result) => {
          this.settings = result
          observer.next(result)
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  // set menue
  setMenue = (menuData: any) => {
    return new Observable<any>((observer) => {
      this.settingsApi.upsertSettingsByType(String(this.project_id), 'general_settings', { menus: JSON.stringify(menuData) })
        .then((response) => {
          observer.next(console.log(response))
          observer.complete()
        })
        .catch((error) => {
          observer.error(error)
        })
    })
  }

  //validate Stripe Api Key — still used by the BYOK path of the Stripe settings
  //popup, which keeps working alongside Stripe Connect (see stripe.provider.ts).
  validateStripeApiKey(apiKey: string) {
    return this.http.post<any>(
      'https://api.stripe.com/v1/customers',
      {},
      {
        headers: {
          Authorization: `Bearer ${apiKey}`
        }
      }
    )
  }

  //validate geidea api key
  validateGeideaToken(token: string) {
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Basic ${token}`
    })

    let body = JSON.stringify({
      amount: 20,
      currency: 'EGP',
      customer: {
        name: 'Kiran',
        email: 'testmail@geidea.com',
        phoneCountryCode: '+20',
        phoneNumber: '8003030083'
      },
      eInvoiceDetails: {
        subtotal: 20,
        grandTotal: 20,
        extraChargesType: 'Amount',
        invoiceDiscountType: 'Amount',
        eInvoiceItems: [
          {
            eInvoiceItemId: '17e95202-d151-43ff-d6af-08db4614f0bb',
            description: 'Subscription for TFM tech',
            price: 10,
            quantity: 2,
            itemDiscountType: 'Amount',
            taxType: 'Amount',
            total: 20
          }
        ]
      }
    })
    const geideaApiUrl = 'https://api.merchant.geidea.net/payment-intent/api/v1/direct/eInvoice'
    return this.http.post<any>(`${environment.proxyUrl}${geideaApiUrl}`, body, { headers: headers })
  }

  saveEmailTemplate(emailForm:any): Observable<any> {
    let postData = new URLSearchParams();
    
    postData.set('project_id', this.project_id);
    postData.set('enableOrderEmail', emailForm.enableOrderEmail ? '1' : '0');
    postData.set('emailTemplate', emailForm.emailTemplate);
    postData.set('emailSubject', emailForm.emailSubject);
    postData.set('emailSignature', emailForm.emailSignature);
    postData.set('senderEmail', emailForm.senderEmail || '');
    postData.set('emailLanguage', emailForm.emailLanguage || 'en');

    return this.http.post(this.builderUrl + 'save_email_template', postData.toString(), this.options);
  }

  saveMetaData(metaForm: any): Observable<any> {
    const postData = new URLSearchParams();

    postData.set('project_id', this.project_id);
    postData.set('meta_pixel_id', metaForm.meta_pixel_id || '');
    postData.set('meta_client_token', metaForm.meta_client_token || '');
    postData.set('facebook_displayName', metaForm.facebook_displayName || '');

    return this.http.post( this.builderUrl + 'save_meta_data', postData.toString(), this.options);
  }

  /**
   * Sends a validation request to PayPal’s OAuth2 API using the provided
   * Base64-encoded credentials (`clientId:secretKey`).
   *
   * This function performs a direct HTTP `POST` request to:
   *    → `${environment.paypal_base}/v1/oauth2/token`
   * Purpose:
   * - Used to verify that the provided PayPal Client ID and Secret Key are valid.
   * - If valid, PayPal returns an object containing an `access_token` and other
   *   metadata (`token_type`, `expires_in`, etc.).
   * - If invalid, PayPal responds with an HTTP 401 error that includes an
   *   `error_description` such as `"Invalid client credentials"`.
   * 
   * @param token Base64 encoded PayPal client credentials (`clientId:secretKey`)
   * @returns Observable<any> Emits the full PayPal API response object:
   *          - On success: `{ access_token, token_type, app_id, expires_in, scope, ... }`
   *          - On failure: HTTP error with `error_description` field.
   */
  validatePaypalToken(token:string): Observable<string> {
  const headers = new HttpHeaders({
    accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `Basic ${token}`
  })

  const body = new URLSearchParams()
  body.set('grant_type', 'client_credentials')
  return this.http.post<any>(`${environment.paypal_base}/v1/oauth2/token` , body.toString(), { headers })
  }

  /**
   * Sends Stripe Connect onboarding data to stacks-agents, which stores it as-is
   * under commerce_settings.stripe.
   */
  saveStripeConnectSettings(data: any): Observable<any> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    return this.http.post<any>(`${environment.agentsUrl}v4/payment/settings/stripe`, { projectId: this.project_id, data }, { headers })
  }

  /**
   * Fetches previously saved Stripe Connect settings so the popup can prefill.
   */
  getStripeConnectSettings(): Observable<any> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    return this.http.post<any>(`${environment.agentsUrl}v4/payment/settings/stripe/showSettings`, { projectId: this.project_id }, { headers })
  }

  /**
   * Creates (or idempotently returns) the project's Stripe Custom Connect account —
   * the actual Stripe API call, as opposed to saveStripeConnectSettings above which
   * only stores the submitted form fields as-is. Only requests the `transfers`
   * capability under the `recipient` service agreement — the account still needs a
   * bank account + ToS acceptance before it can actually receive money, which is
   * what createStripeConnectAccountSession below is for.
   */
  createStripeConnectAccount(dto: Omit<CreateConnectAccountInput, 'projectId'>): Observable<any> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    try{
      return this.http.post<any>(`${environment.agentsUrl}v4/payment/connect/account`, { ...dto, projectId: this.project_id }, { headers })
        .pipe(tap({ error: (e) => console.error('[createStripeConnectAccount]', e) }))
    } catch (e){
      console.error('[createStripeConnectAccount]', e)
      return throwError(e)
    }
  }

  /**
   * Creates a single-use Account Session client secret for mounting Stripe's
   * embedded `account-onboarding` component (via @stripe/connect-js), which is
   * how the merchant actually provides their bank account and accepts Stripe's
   * ToS — collected entirely inside our own UI, they never see stripe.com.
   */
  createStripeConnectAccountSession(): Observable<{ clientSecret: string }> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    return this.http.post<{ clientSecret: string }>(`${environment.agentsUrl}v4/payment/connect/account/${this.project_id}/account-session`, {}, { headers })
  }

  /**
   * Fast DB-only read of the project's Stripe Connect account status (onboarding
   * status, transfers capability, outstanding requirements) — used to drive the
   * "Not connected / Requirements due / Connected" summary on the payments page.
   */
  getStripeConnectStatus(): Observable<any> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    return this.http.get<any>(`${environment.agentsUrl}v4/payment/connect/account/${this.project_id}/status`, { headers })
  }

  /**
   * Live re-sync of the project's Stripe Connect status directly from Stripe.
   */
  refreshStripeConnectStatus(): Observable<any> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.builderService.getToken()}` })
    return this.http.post<any>(`${environment.agentsUrl}v4/payment/connect/account/${this.project_id}/refresh`, {}, { headers })
  }

  /**
   * Loads the order booking schedule from Supabase.
   * Reads the `orderBooking` key from `commerce_settings` in `project_settings` table.
   * The value is stored as a JSON-serialized array.
   * @returns Observable<any[]> that emits the orderBooking array.
   */
  getOrderBookingSchedule () {
    return new Observable<any>((observer) => {
      // getSettingsByType parses each value identically to the old inline
      // JSON.parse; orderBooking is stored as a JSON array string → array.
      // Verified equal to the old direct read against real stored values.
      this.settingsApi.getSettingsByType(String(this.project_id), 'commerce_settings')
        .then((result) => {
          const val = result?.['orderBooking']
          observer.next(Array.isArray(val) ? val : [])
          observer.complete()
        })
        .catch(() => { observer.next([]); observer.complete() })
    })
  }
}
